import React, { useState } from 'react';
import { Gift, CheckCircle2 } from 'lucide-react';
import Modal from './Modal';
import { fmtPeriodo, fmtFechaHora, fmtUsd, periodoActual } from './formato';

const MOTIVOS = ['Cliente nuevo', 'Compensación por falla', 'Promoción', 'Acuerdo comercial'];

const siguienteMes = (periodo) => {
  const [y, m] = periodo.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
};

// Mes de cortesía: esa mensualidad no se cobra. La mensualidad de un mes se
// emite el día 1 del mes siguiente.
const DialogoCortesia = ({ cliente, cobroPendiente, cortesias, onOtorgar, onRevocar, onCancel }) => {
  const [periodo, setPeriodo] = useState(cobroPendiente?.periodo || periodoActual());
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [revocando, setRevocando] = useState(null);

  const afectaCobroEmitido = cobroPendiente && cobroPendiente.periodo === periodo;
  const yaTiene = cortesias.some(c => c.periodo === periodo);

  const otorgar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      await onOtorgar(periodo, motivo.trim());
    } catch (err) {
      setError(err.message || 'No se pudo registrar la cortesía');
      setGuardando(false);
    }
  };

  const revocar = async (c) => {
    setError('');
    setRevocando(c.id);
    try {
      await onRevocar(c);
    } catch (err) {
      setError(err.message || 'No se pudo revocar');
    } finally {
      setRevocando(null);
    }
  };

  return (
    <Modal
      titulo={`Cortesía para ${cliente.slug}`}
      subtitulo="Un mes que no se cobra"
      icono={Gift}
      tono="verde"
      onClose={onCancel}
      bloqueado={guardando}
      ancho="md"
      pie={(
        <>
          <button type="button" onClick={onCancel} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">Cerrar</button>
          <button
            type="submit"
            form="form-cortesia"
            disabled={guardando || yaTiene || motivo.trim().length < 5}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <Gift size={15} /> {guardando ? 'Registrando…' : 'Dar cortesía'}
          </button>
        </>
      )}
    >
      <form id="form-cortesia" onSubmit={otorgar} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Mensualidad de *</span>
          <input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            required
            pattern="\d{4}-\d{2}"
            className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          {periodo && (
            <span className="block mt-1 text-xs text-gray-500">
              Mensualidad de {fmtPeriodo(periodo)}, que se cobra el 1 de {fmtPeriodo(siguienteMes(periodo)).toLowerCase()}.
            </span>
          )}
        </label>

        {afectaCobroEmitido ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Ese cobro ya se emitió ({fmtUsd(cobroPendiente.total_usd)}{cobroPendiente.cotizacion_numero ? `, ${cobroPendiente.cotizacion_numero}` : ''}).
            Se anulará, su cotización quedará rechazada y, si tenía la recarga cortada por él, se le reactiva. No se puede deshacer.
          </div>
        ) : (
          <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            Ese mes no se le generará cobro ni cotización. Se puede revocar mientras no llegue el día de cobro.
          </div>
        )}

        <div>
          <label htmlFor="motivo-cortesia" className="block text-sm font-medium text-gray-700 mb-2">Motivo *</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {MOTIVOS.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setMotivo(m)}
                className={`px-2.5 py-1 rounded-full text-xs border ${motivo === m ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                {m}
              </button>
            ))}
          </div>
          <textarea
            id="motivo-cortesia"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            maxLength={255}
            placeholder="Queda registrado en Punto V"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {yaTiene && <p className="text-xs text-amber-700">Ese mes ya está en cortesía.</p>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

        {cortesias.length > 0 && (
          <section className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Cortesías de este cliente</h4>
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
              {cortesias.map(c => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <CheckCircle2 size={15} className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800">{fmtPeriodo(c.periodo)}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {c.motivo} · {fmtFechaHora(c.created_at)}
                      {c.billing_invoice_id ? ` · anuló ${fmtUsd(c.total_usd)}` : ''}
                    </div>
                  </div>
                  {!c.billing_invoice_id && (
                    <button type="button" onClick={() => revocar(c)} disabled={revocando === c.id} className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-40">
                      Revocar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </form>
    </Modal>
  );
};

export default DialogoCortesia;
